/**
 * Persists last in-app location (pathname + search) per role for restore after session recovery / cold start.
 * Paths are validated against an allowlist — not a substitute for server-side auth.
 */

import { canAccessFeature, resolveLegacyFeatureAlias } from './featureAccess';
import { clearRememberedViews } from './subViewRoutes';
import { clearCobrarSearchSeed } from '../billing/cobrarSearchSeed';
import { SENSITIVE_URL_PARAMS } from '../error/sanitizeUrlForLogging';

const STORAGE_KEY = 'crewvex:lastRoute';

/**
 * Drop any SENSITIVE_URL_PARAMS key (notably the free-text search boxes'
 * `act_q` / `acctact_q` / `pact_q` — a customer name, an email fragment, an
 * IP an admin typed while investigating) from `search` before it is written
 * to localStorage. Same list sanitizeUrlForLogging.js uses to keep this same
 * PII out of the durable client_error_log — reused here so the two paths
 * can't drift on which params count as sensitive.
 */
function stripSensitiveParams(search) {
  if (!search) return '';
  try {
    const params = new URLSearchParams(search);
    let changed = false;
    SENSITIVE_URL_PARAMS.forEach((key) => {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    });
    if (!changed) return search;
    const rest = params.toString();
    return rest ? `?${rest}` : '';
  } catch (_) {
    return search;
  }
}

function pathAllowedForRole(pathname, role) {
  if (!pathname || typeof pathname !== 'string') return false;
  if (pathname === '/') return false;
  if (pathname.startsWith('/log-job/')) {
    return role === 'worker' || role === 'supervisor';
  }
  // RD-NAV-001/002: all four tenant roles resume into the /app/:feature[/<view>]
  // shell. Only the FIRST segment is a `canAccessFeature` feature key — every
  // deeper segment (route/season/opening, jobs/history, account/activity, …)
  // is a sub-view owned by its own registry level (subViewRoutes.js), not a
  // feature in its own right, so validating the WHOLE remainder against
  // canAccessFeature would reject every sub-view URL outright. The remainder
  // (if any) is allowed only if it's plain lowercase/hyphen/slash — the same
  // character class the URL map's slugs are drawn from — so nothing outside
  // that shape (e.g. a stray query-like fragment) can ride through here.
  if (pathname.startsWith('/app/')) {
    const rest = pathname.slice('/app/'.length);
    const [feature, ...restSegments] = rest.split('/');
    // A saved route may predate a slug rename — resolve it so the resume isn't
    // silently downgraded to dashboard (AppShell then redirects to the new URL).
    // A saved legacy `/app/activity` (worker/supervisor's route-board activity
    // feed, before it became a sub-view of `route`) must still restore too —
    // `resolveLegacyFeatureAlias` treats 'activity' as an alias for 'route'
    // for this access check; AppShell's own redirect then maps the full path.
    if (!canAccessFeature(role, resolveLegacyFeatureAlias(feature) ?? feature)) return false;
    if (restSegments.length === 0) return true;
    return /^[a-z0-9-]+(\/[a-z0-9-]+)*$/.test(restSegments.join('/'));
  }
  return false;
}

export function saveLastRoute(pathname, search, role) {
  if (!role || !pathname || pathname === '/') return;
  /**
   * *** THE ALLOWLIST GUARDS THE WRITE, NOT ONLY THE READ. ***
   *
   * `getLastRouteForRole` has always validated on the way OUT, which is enough
   * to stop a bad route being RESTORED — and is NOT enough to stop a credential
   * being STORED. RD-EST-RESP-001 made that concrete: the public estimate page
   * scrubs `?token=<hmac>` out of the address bar with a raw
   * `history.replaceState`, which React Router does not observe (it listens to
   * `popstate` only), so `useLocation().search` on `/estimate` keeps returning
   * the live bearer token for the life of the page. `LastRouteTracker` runs on
   * every route, so a SIGNED-IN visitor opening a customer's estimate link — the
   * founder verifying one, an admin forwarded the email — wrote that token
   * verbatim into `localStorage` under a well-known key, where it survived until
   * the next in-app navigation.
   *
   * Refusing to persist a path the read side would reject anyway costs nothing
   * (a rejected route was never restorable) and closes this for EVERY future
   * public route, not just `/estimate`.
   */
  if (!pathAllowedForRole(pathname, role)) return;
  try {
    const payload = {
      pathname,
      search: stripSensitiveParams(typeof search === 'string' ? search : ''),
      role,
      at: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/**
 * Returns pathname + search for navigate(), or null if none / invalid / wrong role.
 */
/**
 * Drop the saved route (called on sign-out so a shared device can't reveal
 * the prior user's last location). Also clears the in-memory remembered
 * sub-view map (subViewRoutes.js) — every caller of clearLastRoute wants
 * BOTH wiped together (voluntary sign-out, session-revocation watch,
 * membership-role watch, expiry); a caller that clears one but not the other
 * would leave the next signed-in user's sub-tabs seeded from a stranger's
 * last view.
 */
export function clearLastRoute() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
  clearRememberedViews();
  clearCobrarSearchSeed();
}

export function getLastRouteForRole(role) {
  if (!role) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.role !== role || typeof parsed.pathname !== 'string') return null;
    if (!pathAllowedForRole(parsed.pathname, role)) return null;
    const search = typeof parsed.search === 'string' ? parsed.search : '';
    return `${parsed.pathname}${search}`;
  } catch (_) {
    return null;
  }
}
